import { useContext } from 'react';
import * as R from 'ramda';

import { Icon, IconButton, Menu, Snackbar } from '@atom/mui';
import { FileDownloadIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import api from '@atom/utilities/api';
import {
  BudgetTemplateType,
  getDownloadBudgetEndpoint,
} from '@atom/utilities/endpoints';
import { downloadFile } from '@atom/utilities/fileUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import BudgetDetailContext from './BudgetDetailContext';

const { MenuItem } = Menu;
const styles = {
  downloadIcon: {
    marginTop: '1px',
  },
};

const BudgetExportMenu = () => {
  const {
    budget,
    parentBudgetUnit,
    typeFilters,
    categoryFilters,
    budgetItemTemplateFilters,
    excludeZeroBudgetItems,
    showAuthorizedBudget,
  } = useContext(BudgetDetailContext);

  const handleRequestFile = async (type: BudgetTemplateType) => {
    Snackbar.info({
      message: `Downloading ${type === 'authorizedBudget' ? 'authorized' : ''} budget...`,
      autoHideDuration: 5000,
    });

    const requestBody = R.reject(isNilOrEmpty, {
      budgetTypes: typeFilters.map(({ name }) => name),
      categoryIds: categoryFilters.map(({ id }) => id),
      budgetItemTemplateNames: budgetItemTemplateFilters.map(
        ({ name }) => name,
      ),
      excludeZeroBudgetItems,
    });

    const requestParams = {
      budgetUnitId: parentBudgetUnit?.id,
      budgetId: budget.id,
    };

    try {
      const endpoint = getDownloadBudgetEndpoint(type);
      const { data } = await api.post<Blob>(
        endpoint,
        requestBody,
        requestParams,
        undefined,
        { responseType: 'blob' },
      );

      const filename = `${budget.name} - ${type === 'authorizedBudget' ? 'Authorized Budget - ' : ''}${new Date().toISOString().slice(0, 10)}.csv`;

      const url = URL.createObjectURL(data);
      downloadFile(url, filename);

      const message = `Successfully downloaded ${type === 'authorizedBudget' ? 'authorized' : ''} budget.`;
      Snackbar.info({ message });
    } catch (error) {
      const message =
        'There was an error exporting your file, please try again';

      Snackbar.error({
        message,
        autoHideDuration: 10000,
      });
    }
  };

  return showAuthorizedBudget ? (
    <Menu
      icon={FileDownloadIcon}
      IconButtonProps={{ tooltip: 'Download CSV' }}
      IconProps={{
        color: colors.neutral.white,
        style: styles.downloadIcon,
      }}
    >
      <MenuItem onClick={() => handleRequestFile('budgetItems')}>
        Download Budget
      </MenuItem>
      <MenuItem onClick={() => handleRequestFile('authorizedBudget')}>
        Download Authorized Budget
      </MenuItem>
    </Menu>
  ) : (
    <IconButton
      tooltip="Download to CSV"
      onClick={() => handleRequestFile('budgetItems')}
    >
      <Icon style={{ color: colors.neutral.white }} icon={FileDownloadIcon} />
    </IconButton>
  );
};

export default BudgetExportMenu;
